import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  onSnapshot,
  WhereFilterOp,
  Unsubscribe,
  FirestoreDataConverter,
  WriteBatch,
  Transaction
} from 'firebase/firestore';
import type { Guide } from '@segmently/schema-models/auth';
import { CoreFirebaseService } from './CoreFirebaseService';
import { guideConverter as clientGuideConverter } from '../converters/guideConverter';

/**
 * GuideService
 *
 * Type-safe service for Guide entity CRUD operations and real-time subscriptions.
 *
 * Features:
 * - Full CRUD operations (create, read, update, delete, list)
 * - Query operations with filtering
 * - Real-time subscriptions with automatic cleanup
 * - Type-safe: only Guide types exposed, no Firebase types in public API
 * - Uses guideConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { GuideService } from '@/modules/firebase-core/services/GuideService';
 * import { guideConverter } from '@/modules/firebase-core/converters/guideConverter';
 *
 * const service = new GuideService(guideConverter);
 *
 * // Create
 * const guide = await service.create({
 *   name: 'Getting Started',
 *   key: 'getting-started'
 * });
 *
 * // Read
 * const found = await service.get(guide.id);
 *
 * // Update
 * const updated = await service.update(guide.id, { name: 'Updated Guide' });
 *
 * // Delete
 * await service.delete(guide.id);
 *
 * // List with pagination
 * const guides = await service.list({ limit: 10 });
 *
 * // Query
 * const guidesByKey = await service.query('key', '==', 'getting-started');
 *
 * // Real-time subscription
 * const unsubscribe = service.onSnapshot(
 *   guide.id,
 *   (g) => console.log('Updated:', g),
 *   (err) => console.error(err)
 * );
 * // Later: unsubscribe();
 * ```
 */
export class GuideService {
  private readonly collectionName = 'guides';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Guide>;

  /**
   * Create a new GuideService instance
   *
   * @param converter - Firestore converter for Guide entity (defaults to client SDK converter)
   */
  constructor(converter: FirestoreDataConverter<Guide> = clientGuideConverter) {
    this.converter = converter;
  }

  /**
   * Generate a new guide ID without creating the document
   *
   * Useful when you need to know the ID before creating the guide,
   * such as for uploading files to the correct storage path.
   *
   * @returns {string} A new auto-generated ID
   *
   * @example
   * ```typescript
   * const newId = service.generateId();
   * // Upload files to guides/{newId}/...
   * await service.create({ id: newId, name: 'My Guide', ... });
   * ```
   */
  generateId(): string {
    const collectionRef = collection(this.firestore, this.collectionName);
    const docRef = doc(collectionRef);
    return docRef.id;
  }

  /**
   * Create a new guide
   *
   * @param data - Partial guide data (id will be auto-generated if not provided)
   * @returns {Promise<Guide>} The created guide with generated ID
   *
   * @example
   * ```typescript
   * const guide = await service.create({
   *   name: 'Getting Started',
   *   key: 'getting-started',
   *   fullArticleLink: 'https://example.com/guide'
   * });
   * console.log(guide.id); // Auto-generated ID
   *
   * // Or with pre-generated ID:
   * const id = service.generateId();
   * const guide = await service.create({
   *   id,
   *   name: 'Getting Started',
   *   key: 'getting-started'
   * });
   * ```
   */
  async create(data: Partial<Guide>): Promise<Guide> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName);

      // Use provided ID or generate a new one
      const guideId = data.id || doc(collectionRef).id;
      const docRef = doc(collectionRef, guideId).withConverter(this.converter);

      const now = new Date();
      const guideData: Guide = {
        ...data,
        id: guideId,
        name: data.name || '',
        key: data.key || '',
        createdAt: now,
        updatedAt: now,
      } as Guide;

      await setDoc(docRef, guideData);

      return guideData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.create');
      throw error;
    }
  }

  /**
   * Get a guide by ID
   *
   * @param id - Guide ID
   * @returns {Promise<Guide | null>} The guide or null if not found
   *
   * @example
   * ```typescript
   * const guide = await service.get('guide-123');
   * if (guide) {
   *   console.log(guide.name);
   * }
   * ```
   */
  async get(id: string): Promise<Guide | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Guide;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.get');
      throw error;
    }
  }

  /**
   * Update an existing guide
   *
   * @param id - Guide ID
   * @param data - Partial guide data to update
   * @returns {Promise<Guide>} The updated guide
   *
   * @example
   * ```typescript
   * const updated = await service.update('guide-123', {
   *   name: 'Updated Name',
   *   fullArticleLink: 'https://example.com/updated'
   * });
   * ```
   */
  async update(id: string, data: Partial<Guide>): Promise<Guide> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use setDoc with merge to go through converter
      await setDoc(docRef, updateData as Guide, { merge: true });

      // Fetch and return updated document
      const updated = await this.get(id);
      if (!updated) {
        throw new Error(`Guide ${id} not found after update`);
      }

      return updated;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.update');
      throw error;
    }
  }

  /**
   * Delete a guide
   *
   * @param id - Guide ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('guide-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.delete');
      throw error;
    }
  }

  /**
   * List guides with optional pagination
   *
   * @param options - Query options
   * @param options.limit - Maximum number of guides to return
   * @param options.startAfter - Start after this value (for pagination)
   * @returns {Promise<Guide[]>} Array of guides
   *
   * @example
   * ```typescript
   * // First page
   * const firstPage = await service.list({ limit: 10 });
   *
   * // Next page
   * const lastGuide = firstPage[firstPage.length - 1];
   * const nextPage = await service.list({
   *   limit: 10,
   *   startAfter: lastGuide.createdAt
   * });
   * ```
   */
  async list(options: { limit?: number; startAfter?: any } = {}): Promise<Guide[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName).withConverter(this.converter);

      let q = query(collectionRef, orderBy('createdAt', 'desc'));

      if (options.limit) {
        q = query(q, limit(options.limit));
      }

      if (options.startAfter) {
        q = query(q, startAfter(options.startAfter));
      }

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Guide));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.list');
      throw error;
    }
  }

  /**
   * Query guides by field
   *
   * @param field - Field name to query
   * @param operator - Comparison operator
   * @param value - Value to compare against
   * @returns {Promise<Guide[]>} Array of matching guides
   *
   * @example
   * ```typescript
   * // Find guide by key
   * const guides = await service.query('key', '==', 'getting-started');
   *
   * // Find guides created after a date
   * const recentGuides = await service.query(
   *   'createdAt',
   *   '>',
   *   '2025-01-01'
   * );
   * ```
   */
  async query(field: string, operator: WhereFilterOp, value: any): Promise<Guide[]> {
    try {
      const collectionRef = collection(this.firestore, this.collectionName).withConverter(this.converter);
      const q = query(collectionRef, where(field, operator, value));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Guide));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.query');
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a guide
   *
   * @param id - Guide ID
   * @param onNext - Callback when guide is updated (receives Guide | null)
   * @param onError - Callback when an error occurs
   * @returns {Unsubscribe} Function to call to unsubscribe
   *
   * @example
   * ```typescript
   * const unsubscribe = service.onSnapshot(
   *   'guide-123',
   *   (guide) => {
   *     if (guide) {
   *       console.log('Guide updated:', guide.name);
   *     } else {
   *       console.log('Guide deleted');
   *     }
   *   },
   *   (error) => console.error('Subscription error:', error)
   * );
   *
   * // Later: cleanup subscription
   * unsubscribe();
   * ```
   */
  onSnapshot(
    id: string,
    onNext: (guide: Guide | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    try {
      const docRef = doc(this.firestore, this.collectionName, id).withConverter(this.converter);

      return onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            // Get data through converter
            const data = snapshot.data();
            // Add id field
            const guide: Guide = { ...data, id: snapshot.id };
            onNext(guide);
          } else {
            onNext(null);
          }
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'GuideService.onSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.onSnapshot.setup');
      throw error;
    }
  }

  /**
   * Update a guide in a batch operation
   *
   * @param batch - Firestore WriteBatch instance
   * @param id - Guide ID
   * @param data - Partial guide data to update
   *
   * @example
   * ```typescript
   * const batch = core.batch();
   * service.batchUpdate(batch, 'guide-1', { name: 'Updated 1' });
   * service.batchUpdate(batch, 'guide-2', { name: 'Updated 2' });
   * await batch.commit();
   * ```
   */
  batchUpdate(batch: WriteBatch, id: string, data: Partial<Guide>): void {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use converter to transform TypeScript entity → Firestore data
      // Converts ISO strings → Timestamps
      const firestoreData = this.converter.toFirestore(updateData as Guide);

      // Use batch.update() to update ONLY specified fields (not set with merge)
      batch.update(docRef, firestoreData);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.batchUpdate');
      throw error;
    }
  }

  /**
   * Delete a guide in a batch operation
   *
   * @param batch - Firestore WriteBatch instance
   * @param id - Guide ID
   *
   * @example
   * ```typescript
   * const batch = core.batch();
   * service.batchDelete(batch, 'guide-1');
   * service.batchDelete(batch, 'guide-2');
   * await batch.commit();
   * ```
   */
  batchDelete(batch: WriteBatch, id: string): void {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      batch.delete(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.batchDelete');
      throw error;
    }
  }

  /**
   * Get a guide within a transaction
   *
   * @param transaction - Firestore Transaction instance
   * @param id - Guide ID
   * @returns {Promise<Guide | null>} The guide or null if not found
   *
   * @example
   * ```typescript
   * await core.runTransaction(async (txn) => {
   *   const guide = await service.transactionGet(txn, 'guide-123');
   *   if (guide) {
   *     await service.transactionUpdate(txn, guide.id, {
   *       counter: (guide.counter || 0) + 1
   *     });
   *   }
   * });
   * ```
   */
  async transactionGet(transaction: Transaction, id: string): Promise<Guide | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);
      const docSnap = await transaction.get(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      // Use converter to transform Firestore data → TypeScript entity
      // Converts Timestamps → ISO strings
      const guide = this.converter.fromFirestore(docSnap as any, {});
      return { ...guide, id: docSnap.id } as Guide;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.transactionGet');
      throw error;
    }
  }

  /**
   * Update a guide within a transaction
   *
   * @param transaction - Firestore Transaction instance
   * @param id - Guide ID
   * @param data - Partial guide data to update
   *
   * @example
   * ```typescript
   * await core.runTransaction(async (txn) => {
   *   const guide = await service.transactionGet(txn, 'guide-123');
   *   await service.transactionUpdate(txn, 'guide-123', {
   *     counter: (guide?.counter || 0) + 1
   *   });
   * });
   * ```
   */
  async transactionUpdate(transaction: Transaction, id: string, data: Partial<Guide>): Promise<void> {
    try {
      const docRef = doc(this.firestore, this.collectionName, id);

      const updateData = {
        ...data,
        updatedAt: new Date(),
      };

      // Use converter to transform TypeScript entity → Firestore data
      // Converts ISO strings → Timestamps
      const firestoreData = this.converter.toFirestore(updateData as Guide);

      // Use transaction.update() to update ONLY specified fields (not set with merge)
      transaction.update(docRef, firestoreData);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'GuideService.transactionUpdate');
      throw error;
    }
  }
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

/**
 * Create a mock GuideService for testing
 *
 * Usage in tests:
 * ```typescript
 * import { createMockGuideService } from '@modules/firebase-core/services/GuideService';
 *
 * const mockService = createMockGuideService({
 *   get: jest.fn().mockResolvedValue(mockGuide),
 * });
 * ```
 *
 * @param overrides - Optional method overrides
 * @returns Mocked GuideService instance
 */
export function createMockGuideService(
  overrides: Partial<jest.Mocked<GuideService>> = {}
): jest.Mocked<GuideService> {
  return {
    // CRUD operations
    list: jest.fn().mockResolvedValue([]),
    get: jest.fn().mockResolvedValue(null),
    create: jest.fn().mockResolvedValue({ id: 'new-guide-id', name: '', key: '' }),
    update: jest.fn().mockResolvedValue({ id: 'updated-guide-id', name: '', key: '' }),
    delete: jest.fn().mockResolvedValue(undefined),
    query: jest.fn().mockResolvedValue([]),
    onSnapshot: jest.fn().mockReturnValue(() => {}),

    // Batch operations
    batchUpdate: jest.fn(),
    batchDelete: jest.fn(),

    // Transaction operations
    transactionGet: jest.fn().mockResolvedValue(null),
    transactionUpdate: jest.fn().mockResolvedValue(undefined),

    // Allow overrides
    ...overrides,
  } as unknown as jest.Mocked<GuideService>;
}
